// hooks/useComplexOrderForm.ts
import React, { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  OptionItemInterface,
  ComplexOrderInterface,
  SaleItemInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useInput } from '../../../../../../common/hooks/useInput'
import { processAttributes } from '../../../../../../common/utils/functions/processAttributes'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { useSelector } from 'react-redux'
import { selectCurrentCluster } from '../../../../../../store/slices/currentClusterSlice'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { ComplexOrderStatus } from '../../../../../../common/types/enums'
import { complexOrderValidations } from '../../../../../../common/utils/constants'

const complexOrderTransitions: Record<number, number[]> = {
  [ComplexOrderStatus.DRAFT]: [ComplexOrderStatus.SENT_TO_MANAGER, ComplexOrderStatus.IN_PROGRESS],
  [ComplexOrderStatus.SENT_TO_MANAGER]: [
    ComplexOrderStatus.IN_PROGRESS,
    ComplexOrderStatus.APPROVED,
  ],
  [ComplexOrderStatus.IN_PROGRESS]: [ComplexOrderStatus.APPROVED, ComplexOrderStatus.ARCHIVED],
  [ComplexOrderStatus.APPROVED]: [ComplexOrderStatus.CONVERTED, ComplexOrderStatus.ARCHIVED],
  [ComplexOrderStatus.CONVERTED]: [ComplexOrderStatus.ARCHIVED],
  [ComplexOrderStatus.ARCHIVED]: [],
}



export const useForm = (
  initialData: ComplexOrderInterface,
  setData: React.Dispatch<SetStateAction<ComplexOrderInterface | undefined>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  modalActive: boolean,
  data?: ComplexOrderInterface,
  el?: ComplexOrderInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [saleItemList, setSaleItemList] = useState<SaleItemInterface[]>([])
  const [adding, setAdding] = useState(false)
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const listRef = useRef<HTMLDivElement>(null)
  const currentCluster = useSelector(selectCurrentCluster)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()
  const [originalStatus, setOriginalStatus] = useState<number | undefined>(data?.status)

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    complexOrderValidations,
  )

  const { inputHandler } = useInput(setData, checkAndAddValidation)

  useEffect(() => {
    if (data?.id) {
      setOriginalStatus(data.status)
    }
  }, [data?.id])

  useEffect(() => {
    if (el) {
      setData(structuredClone(el))
      return
    }
    setData({ ...initialData })
  }, [el, setData, initialData])

  const saleItemMap = useMemo(
    () => new Map(saleItemList.map((item) => [item.id, item])),
    [saleItemList],
  )

  const getSaleItemList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) setSaleItemList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const createSaleItemSelectList = useCallback(() => {
    let arr: OptionItemInterface[] = []
    for (const item of saleItemList) {
      const attrsString = processAttributes(item.attributes)
      arr.push({
        id: String(item.id),
        label: '#' + item.id + ' ' + item.name + attrsString,
        name: item.name,
        value: String(item.id),
      })
    }
    return arr
  }, [saleItemList])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const disabled = validationResult.some((el) => el.notValid)

  const formAction = useCallback(async () => {
    try {
      if (currentCluster) {
        await showCommonLoader()
        await delayAction(1000)
        if (data?.id) {
          const { createdAt, updatedAt, ...rest } = data
          const cleanData = { ...rest }
          if (cleanData.items) {
            cleanData.items = cleanData.items.map(({ id, ...item }) => item)
          }
          await salesApi.updateComplexOrder(cleanData)
        }
        await getList()
        await hideCommonLoader()
        setModalActive(false)
      }
    } catch (error) {
      await hideCommonLoader()
    }
  }, [salesApi, data, getList, setModalActive, currentCluster, showCommonLoader, hideCommonLoader])

  const deleteItem = useCallback(
    (itemId: number) => {
      setData((prev) => {
        if (!prev) return prev
        const items = prev.items?.filter((item) => item.itemId !== itemId) || []
        const total = items.reduce(
          (sum, item) => sum + (item.manualPrice ?? item.basePrice) * item.quantity,
          0,
        )
        return { ...prev, items, total }
      })
    },
    [setData],
  )

  const addItem = useCallback(
    (saleItemId: number) => {
      const saleItem = saleItemMap.get(saleItemId)
      setData((prev) => {
        if (!prev) return prev
        const newItem = {
          itemId: saleItemId,
          name: saleItem?.name || `Товар #${saleItemId}`,
          quantity: 1,
          basePrice: saleItem?.prices?.[0]?.price || 0,
          manualPrice: null,
          total: saleItem?.prices?.[0]?.price || 0,
        }
        const updatedItems = [...(prev.items || []), newItem]
        const total = updatedItems.reduce(
          (sum, item) => sum + (item.manualPrice ?? item.basePrice) * item.quantity,
          0,
        )
        return { ...prev, items: updatedItems, total }
      })
      setAdding(false)
    },
    [setData, saleItemMap],
  )

  const updateItem = useCallback(
    (itemId: number, field: 'quantity' | 'manualPrice', value: string) => {
      setData((prev) => {
        if (!prev || !prev.items) return prev
        const updatedItems = prev.items.map((item) => {
          if (item.itemId !== itemId) return item
          let filtered = value
            .replace(/[^\d.]/g, '')
            .replace(/\.(?=.*\.)/g, '')
            .replace(/^0+(?!\.|$)/, '')
          if (filtered.length > 10) filtered = filtered.slice(0, 10)
          const numericValue = parseFloat(filtered || '0') || 0
          const updated = {
            ...item,
            [field]: field === 'manualPrice' ? numericValue || null : numericValue,
          }
          const price = updated.manualPrice ?? updated.basePrice
          updated.total = price * updated.quantity
          return updated
        })
        const total = updatedItems.reduce((sum, item) => sum + item.total, 0)
        return { ...prev, items: updatedItems, total }
      })
    },
    [setData],
  )

  const getDisabledStatuses = useCallback((): number[] => {
    if (!data?.id) {
      const allStatuses = Object.values(ComplexOrderStatus).filter(
        (v): v is number => typeof v === 'number',
      )
      return allStatuses.filter((s) => s !== ComplexOrderStatus.DRAFT)
    }
    if (originalStatus === undefined) return []
    const allowed = complexOrderTransitions[originalStatus] || []
    const allStatuses = Object.values(ComplexOrderStatus).filter(
      (v): v is number => typeof v === 'number',
    )
    return allStatuses.filter((s) => s !== originalStatus && !allowed.includes(s))
  }, [data?.id, originalStatus])

  useEffect(() => {
    getSaleItemList()
  }, [getSaleItemList])

  useEffect(() => {
    if (!data) return
    validate('phone_number', data.phone_number || '', 'text')
    validate('customerName', data.customerName || '', 'text')
  }, [data, validate])

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
    }
  }, [data?.items?.length])

  useEffect(() => {
    return () => {
      if (!modalActive) setData({ ...initialData })
    }
  }, [modalActive, initialData, setData])

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    deleteItem,
    adding,
    setAdding,
    createSaleItemSelectList,
    handleAddItem: (value: string | number | boolean, fieldName: string, type: string) => {
      if (type === 'add_item') {
        const id = Number(value)
        if (id > 0) addItem(id)
      }
    },
    updateItem,
    listRef,
    getDisabledStatuses,
  }
}
